<template>
    <div>
        <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
                <v-btn
                    class="feedback-launcher"
                    fab
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    aria-label="Send feedback"
                    @click="openGeneralFeedback"
                >
                    <v-icon>mdi-message-text-outline</v-icon>
                </v-btn>
            </template>
            <span>Send feedback</span>
        </v-tooltip>

        <v-dialog v-model="open" max-width="520" persistent>
            <v-card class="feedback-card">
                <v-card-title>
                    {{ title }}
                    <v-spacer></v-spacer>
                    <v-btn icon aria-label="Close feedback" @click="closeFeedback">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-text>
                    <p>{{ prompt }}</p>
                    <v-select
                        v-model="category"
                        :items="categories"
                        item-text="label"
                        item-value="value"
                        label="Choose one"
                        outlined
                        dense
                    />
                    <v-textarea
                        v-model="message"
                        :label="type === 'general' ? 'Feedback' : 'Anything you would like to add? (optional)'"
                        :counter="2000"
                        :rules="messageRules"
                        outlined
                        rows="4"
                    />
                    <v-checkbox
                        v-model="contactPermitted"
                        :label="contactPermissionLabel"
                    />
                    <div class="feedback-recaptcha">
                        <div ref="recaptchaV2Container"></div>
                    </div>
                    <p class="recaptcha-notice">
                        This site is protected by reCAPTCHA and the Google
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                        and
                        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                        apply.
                    </p>
                    <v-alert v-if="error" type="error" dense outlined>{{ error }}</v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeFeedback">Cancel</v-btn>
                    <v-btn
                        color="primary"
                        :loading="submitting"
                        :disabled="!canSubmit"
                        @click="submitFeedback"
                    >
                        Send
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackbar" bottom>
            {{ snackbarText }}
            <v-btn text @click="snackbar = false">Close</v-btn>
        </v-snackbar>
    </div>
</template>

<script lang="ts">
import Vue from 'vue'
import ErrorHelper from './ErrorHelper'
import { FeedbackPrompt, FeedbackType } from './Feedback'
import FeedbackService from './FeedbackService'
import RecaptchaClientService from './RecaptchaClientService'
import { loadRecaptchaScript } from './RecaptchaLoader'
import RecaptchaProtectedResponse from './RecaptchaProtectedResponse'
import StorageHelper from './StorageHelper'
import UserEventService from './UserEventService'

const generalCategories = [
    { value: 'issue', label: 'Something did not work' },
    { value: 'feature_request', label: 'Missing feature' },
    { value: 'difficult_to_use', label: 'Difficult to use' },
    { value: 'pricing_subscription', label: 'Pricing or subscription' },
    { value: 'privacy_trust', label: 'Privacy or trust' },
    { value: 'other', label: 'Other' }
]

const upgradeCategories = [
    { value: 'evaluating', label: 'I am only evaluating it' },
    { value: 'one_route', label: 'I only need it for one route' },
    { value: 'too_expensive', label: 'It costs too much' },
    { value: 'payment_options', label: 'The payment options do not suit me' },
    { value: 'insufficient_value', label: 'I have not seen enough value yet' },
    { value: 'trust', label: 'I am unsure whether I can trust it' },
    { value: 'missing_feature', label: 'A feature I need is missing' },
    { value: 'technical_problem', label: 'I could not get it to work' },
    { value: 'other', label: 'Other' }
]

export default Vue.extend({
    name: 'FeedbackWidget',
    data: () => ({
        open: false,
        type: 'general' as FeedbackType,
        promptContext: 'passive',
        restrictionKey: null as string|null,
        category: '',
        message: '',
        contactPermitted: false,
        submitting: false,
        submitted: false,
        error: '',
        recaptchaV2SiteKey: '',
        recaptchaV3SiteKey: '',
        recaptchaReady: false,
        requireRecaptchaV2: true,
        recaptchaV2Token: null as string|null,
        recaptchaV2WidgetId: null as number|null,
        snackbar: false,
        snackbarText: ''
    }),
    computed: {
        feedbackPrompt(): FeedbackPrompt|null {
            return this.$store.getters.feedbackPrompt
        },
        title(): string {
            return this.type === 'general' ? 'Send feedback' : 'One quick question'
        },
        prompt(): string {
            return this.type === 'general'
                ? 'What could GPX Planner do better?'
                : 'What is stopping you from upgrading today?'
        },
        categories(): Array<{value: string; label: string}> {
            return this.type === 'general' ? generalCategories : upgradeCategories
        },
        messageRules(): Array<(value: string) => boolean|string> {
            return [(value: string) => (value || '').length <= 2000 || 'Feedback must be 2,000 characters or fewer']
        },
        contactPermissionLabel(): string {
            return this.$store.getters.isUserLoggedIn
                ? 'You may contact me about this'
                : 'You may contact me about this (please include contact details in your feedback)'
        },
        canSubmit(): boolean {
            const hasMessage = this.type !== 'general' || this.message.trim().length > 0
            const hasCaptcha = !this.requireRecaptchaV2 || !!this.recaptchaV2Token
            return !!this.category && hasMessage && this.message.length <= 2000 && hasCaptcha && !this.submitting
        }
    },
    watch: {
        feedbackPrompt(prompt: FeedbackPrompt|null) {
            if(prompt) {
                this.openFeedback(prompt)
                this.$store.commit('clearFeedbackPrompt')
            }
        }
    },
    methods: {
        openGeneralFeedback() {
            this.openFeedback({ type: 'general', promptContext: 'passive' })
        },
        async openFeedback(prompt: FeedbackPrompt) {
            this.type = prompt.type
            this.promptContext = prompt.promptContext
            this.restrictionKey = prompt.restrictionKey || null
            this.category = ''
            this.message = ''
            this.contactPermitted = false
            this.error = ''
            this.submitted = false
            this.requireRecaptchaV2 = true
            this.recaptchaV2Token = null
            this.open = true
            if(prompt.type === 'upgrade_blocker') {
                StorageHelper.setItem(this.upgradePromptStorageKey(), 'true')
            }
            UserEventService.recordSafe('feedback_opened', {
                feedbackType: this.type,
                promptContext: this.promptContext,
                restrictionKey: this.restrictionKey
            })
            await this.initializeRecaptcha()
            if(this.recaptchaReady) {
                await this.$nextTick()
                this.ensureV2Widget()
            }
        },
        async initializeRecaptcha() {
            if(this.recaptchaReady) return
            try {
                const response = await RecaptchaClientService.getConfig()
                this.recaptchaV2SiteKey = response.data.recaptchaV2SiteKey
                this.recaptchaV3SiteKey = response.data.recaptchaV3SiteKey
                await loadRecaptchaScript(this.recaptchaV3SiteKey)
                this.recaptchaReady = true
            } catch(error) {
                console.error('Feedback reCAPTCHA initialization failed', error)
                this.error = 'Could not load spam protection. Please try again.'
            }
        },
        async submitFeedback() {
            if(!this.canSubmit) return
            this.submitting = true
            this.error = ''
            try {
                await this.initializeRecaptcha()
                await FeedbackService.submit({
                    type: this.type,
                    category: this.category,
                    message: this.message.trim(),
                    contactPermitted: this.contactPermitted,
                    page: this.$route.fullPath,
                    promptContext: this.promptContext,
                    restrictionKey: this.restrictionKey,
                    requireRecaptchaV2: this.requireRecaptchaV2,
                    recaptchaV2Token: this.recaptchaV2Token,
                    recaptchaV3Token: null
                })
                this.submitted = true
                UserEventService.recordSafe('feedback_submitted', {
                    feedbackType: this.type,
                    category: this.category,
                    promptContext: this.promptContext,
                    restrictionKey: this.restrictionKey
                })
                this.open = false
                this.snackbarText = 'Thank you. Your feedback has been sent.'
                this.snackbar = true
            } catch(error) {
                const response = (error as any)?.response?.data as RecaptchaProtectedResponse|undefined
                if(response?.requireRecaptchaV2) {
                    this.requireRecaptchaV2 = true
                    this.recaptchaV2Token = null
                    this.error = response.message || 'Please complete the reCAPTCHA challenge.'
                    await this.$nextTick()
                    this.ensureV2Widget()
                } else if(!ErrorHelper.ifUserError(error, message => this.error = message)) {
                    this.error = 'Could not send feedback. Please try again.'
                }
            } finally {
                this.submitting = false
            }
        },
        ensureV2Widget() {
            if(typeof window.grecaptcha?.render !== 'function' ||
                typeof window.grecaptcha?.reset !== 'function' || !this.recaptchaV2SiteKey) {
                this.error = 'Could not load spam protection. Please close the form and try again.'
                return
            }
            const container = this.$refs.recaptchaV2Container as HTMLElement|undefined
            if(!container) return
            if(this.recaptchaV2WidgetId === null) {
                this.recaptchaV2WidgetId = window.grecaptcha.render(container, {
                    sitekey: this.recaptchaV2SiteKey,
                    callback: (token: string) => this.recaptchaV2Token = token,
                    'expired-callback': () => this.recaptchaV2Token = null,
                    'error-callback': () => this.recaptchaV2Token = null
                })
            } else {
                window.grecaptcha.reset(this.recaptchaV2WidgetId)
            }
        },
        closeFeedback() {
            if(!this.submitted) {
                UserEventService.recordSafe('feedback_dismissed', {
                    feedbackType: this.type,
                    promptContext: this.promptContext,
                    restrictionKey: this.restrictionKey
                })
            }
            this.open = false
        },
        upgradePromptStorageKey() {
            return `UpgradeFeedbackPrompted:${this.$store.getters.policy?.version || 'unknown'}`
        }
    }
})
</script>

<style scoped>
.feedback-launcher {
    position: fixed;
    right: max(16px, env(safe-area-inset-right));
    bottom: max(16px, env(safe-area-inset-bottom));
    z-index: 8;
}

.feedback-recaptcha {
    min-height: 78px;
}

.recaptcha-notice {
    color: rgba(0, 0, 0, 0.62);
    font-size: 0.75rem;
    line-height: 1.35;
    margin: 6px 0 0;
}

@media (max-width: 400px) {
    .feedback-card > .v-card__text {
        padding-left: 18px;
        padding-right: 18px;
    }
}
</style>
